import { Alert, App, Button, DatePicker, Form, Input, Select, Space, Table, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import { ExternalLink, Plus, RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { crestHttp } from '../../api/client.js';
import { EmptyState, FancyModal } from '../../components/ui/index.js';
import {
  AccessScopeSelector,
  type ScopePolicy,
  defaultAccessScopeConfig,
  hasInvalidSelectedScope,
} from './AccessScopeSelector.js';
import type { AccessAssignmentRow, RoleCatalogRow, UserOption } from './access-center-types.js';

interface RequestForm {
  userId: string;
  roleCatalogId: string;
  sourceType: 'direct' | 'delegation' | 'emergency';
  delegatedFromUserId?: string;
  reason: string;
  validFrom?: Dayjs;
  validTo?: Dayjs;
}

const APPROVAL_STATES = new Set([
  'approval_pending',
  'approval_failed',
  'approval_rejected',
  'approval_cancelled',
]);

export function AccessRequestsPanel() {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [form] = Form.useForm<RequestForm>();
  const sourceType = Form.useWatch('sourceType', form) ?? 'direct';
  const roleId = Form.useWatch('roleCatalogId', form);
  const [rows, setRows] = useState<AccessAssignmentRow[]>([]);
  const [roles, setRoles] = useState<RoleCatalogRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [scopePolicy, setScopePolicy] = useState<ScopePolicy>('role_default');
  const [scopeConfig, setScopeConfig] = useState(defaultAccessScopeConfig);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      crestHttp.get<{ rows: AccessAssignmentRow[] }>('/admin/access/assignments', {
        params: { pageSize: 200 },
      }),
      crestHttp.get<{ rows: RoleCatalogRow[] }>('/admin/access/roles', {
        params: { pageSize: 200, status: 'active' },
      }),
      crestHttp.get<{ rows: UserOption[] }>('/admin/users', { params: { pageSize: 500 } }),
    ])
      .then(([assignments, catalog, userRows]) => {
        setRows(
          (assignments.data.rows ?? []).filter(
            (row) =>
              Boolean(row.workflow_instance_id) ||
              APPROVAL_STATES.has(row.state) ||
              ['delegation', 'emergency'].includes(row.source_type),
          ),
        );
        setRoles(catalog.data.rows ?? []);
        setUsers(userRows.data.rows ?? []);
      })
      .catch(() => message.error(t('loadFailed')))
      .finally(() => setLoading(false));
  }, [message, t]);

  useEffect(() => load(), [load]);

  const requestableRoles = useMemo(
    () =>
      sourceType === 'direct'
        ? roles.filter(
            (role) =>
              ['system', 'admin'].includes(role.role_type) ||
              role.metadata?.requiresApproval === true ||
              role.metadata?.hasGlobalAccess === true,
          )
        : roles,
    [roles, sourceType],
  );
  const selectedRole = useMemo(
    () => requestableRoles.find((role) => role.role_catalog_id === roleId),
    [requestableRoles, roleId],
  );

  const submit = async () => {
    const values = await form.validateFields();
    if (scopePolicy === 'explicit' && hasInvalidSelectedScope(scopeConfig)) {
      message.error(t('scopeRequired'));
      return;
    }
    try {
      await crestHttp.post('/admin/access/requests', {
        ...values,
        scopePolicy: selectedRole?.domain === 'crm' ? scopePolicy : 'role_default',
        scopeConfig:
          selectedRole?.domain === 'crm' && scopePolicy === 'explicit' ? scopeConfig : {},
        validFrom: values.validFrom?.toISOString(),
        validTo: values.validTo?.toISOString(),
      });
      message.success(t('requestSubmitted'));
      setOpen(false);
      form.resetFields();
      resetScope();
      load();
    } catch {
      message.error(t('requestSubmitFailed'));
      load();
    }
  };

  const resetScope = () => {
    setScopePolicy('role_default');
    setScopeConfig(defaultAccessScopeConfig());
  };

  const retry = async (id: string) => {
    await crestHttp.post(`/admin/access/requests/${id}/retry`);
    message.success(t('requestRetryQueued'));
    load();
  };

  return (
    <div className="crest-list-card">
      <Alert
        showIcon
        type="info"
        message={t('approvalPolicy')}
        description={t('approvalPolicyDescription')}
        style={{ marginBottom: 12 }}
      />
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
          {t('newAccessRequest')}
        </Button>
        <Button icon={<RefreshCw size={14} />} onClick={load}>
          {t('common:refresh')}
        </Button>
      </Space>
      <Table
        rowKey="access_assignment_id"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <EmptyState title={t('noAccessRequests')} compact /> }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        columns={[
          { title: t('user'), render: (_, row) => row.user?.fullname ?? '-' },
          {
            title: t('roleName'),
            render: (_, row) =>
              row.role ? `[${row.role.domain.toUpperCase()}] ${row.role.name}` : '-',
          },
          { title: t('requestType'), dataIndex: 'source_type', width: 120 },
          {
            title: t('dataScope'),
            width: 170,
            render: (_, row) => t(`scope_${row.scope_policy ?? 'role_default'}`),
          },
          {
            title: t('requestReason'),
            render: (_, row) => row.scope_config?.reason ?? '-',
            ellipsis: true,
          },
          {
            title: t('common:status'),
            dataIndex: 'state',
            width: 150,
            render: (value) => <Tag>{t(`state_${value}`)}</Tag>,
          },
          {
            title: t('validTo'),
            dataIndex: 'valid_to',
            width: 180,
            render: (value) => (value ? new Date(value).toLocaleString() : t('indefinite')),
          },
          {
            title: t('actions'),
            width: 140,
            render: (_, row) => (
              <Space size={0}>
                {row.state === 'approval_failed' && (
                  <Button
                    type="link"
                    icon={<RotateCcw size={13} />}
                    onClick={() => retry(row.access_assignment_id)}
                  >
                    {t('retry')}
                  </Button>
                )}
                {row.workflow_instance_id && (
                  <Link to={`/app/workflow/instances/${row.workflow_instance_id}`}>
                    <ExternalLink size={14} aria-hidden="true" /> {t('viewWorkflow')}
                  </Link>
                )}
              </Space>
            ),
          },
        ]}
      />
      <FancyModal
        title={t('newAccessRequest')}
        subtitle={t('accessRequestSubtitle')}
        icon={<Plus size={18} />}
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
          resetScope();
        }}
        onOk={submit}
      >
        <Form form={form} layout="vertical" initialValues={{ sourceType: 'direct' }}>
          <Form.Item name="sourceType" label={t('requestType')} rules={[{ required: true }]}>
            <Select
              options={['direct', 'delegation', 'emergency'].map((value) => ({
                value,
                label: t(`source_${value}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="userId" label={t('user')} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={userOptions(users)} />
          </Form.Item>
          {sourceType === 'delegation' && (
            <Form.Item
              name="delegatedFromUserId"
              label={t('delegatedFrom')}
              rules={[{ required: true }]}
            >
              <Select showSearch optionFilterProp="label" options={userOptions(users)} />
            </Form.Item>
          )}
          <Form.Item name="roleCatalogId" label={t('roleName')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={requestableRoles.map((role) => ({
                value: role.role_catalog_id,
                label: `[${role.domain.toUpperCase()}] ${role.name}`,
              }))}
            />
          </Form.Item>
          <AccessScopeSelector
            role={selectedRole}
            mode="grant"
            scopePolicy={scopePolicy}
            scopeConfig={scopeConfig}
            onPolicyChange={(value) => {
              setScopePolicy(value);
              setScopeConfig(defaultAccessScopeConfig());
            }}
            onScopeConfigChange={setScopeConfig}
          />
          <Form.Item name="reason" label={t('requestReason')} rules={[{ required: true, min: 10 }]}>
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          <Space align="start">
            <Form.Item name="validFrom" label={t('validFrom')}>
              <DatePicker showTime />
            </Form.Item>
            <Form.Item
              name="validTo"
              label={t('validTo')}
              rules={[
                {
                  required: sourceType === 'delegation' || sourceType === 'emergency',
                  message: t('expiryRequired'),
                },
              ]}
            >
              <DatePicker showTime />
            </Form.Item>
          </Space>
        </Form>
      </FancyModal>
    </div>
  );
}

function userOptions(users: UserOption[]) {
  return users.map((user) => ({
    value: user.systemuserid,
    label: `${user.fullname} · ${user.domainname ?? ''}`,
  }));
}
